import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useAppContext } from "../common/utils/CustomHooks/useAppContext";

export const ContactPage = () => {
  const { appState, updateContactFormDetails } = useAppContext();
  const { name, email, message } = appState.contactFormDetails;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 },
    },
  };

  const handleSendEmail = () => {
    const subject = encodeURIComponent(`New Contact Message from ${name}`);
    const body = encodeURIComponent(
      `Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`
    );
    window.location.href = `mailto:theblinddate.in@gmail.com?subject=${subject}&body=${body}`;
  };

  const updateField = (field: string, value: string) => {
    updateContactFormDetails({
      ...appState.contactFormDetails,
      [field]: value,
    });
  };

  return (
    <main className="min-h-screen bg-background overflow-hidden">
      {/* ===== BREADCRUMB ===== */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-4">
        <p className="text-sm text-foreground/60">
          <Link to="/" className="hover:text-primary">
            Home
          </Link>{" "}
          / Contact
        </p>
      </div>

      {/* HERO */}
      <motion.section
        className="relative min-h-[5vh] flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 pt-24 text-center"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="absolute top-0 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/5 rounded-full blur-3xl pointer-events-none" />

        <motion.h1
          className="text-5xl sm:text-6xl lg:text-7xl font-light italic text-primary mb-6 tracking-tight"
          variants={itemVariants}
        >
          Contact Us
        </motion.h1>

        <motion.p
          className="text-lg sm:text-xl text-foreground/70 max-w-2xl leading-relaxed"
          variants={itemVariants}
        >
          We'd love to hear from you. Whether it's a question, suggestion, or
          just a hello—reach out, and we'll get back to you soon.
        </motion.p>
      </motion.section>

      {/* CONTACT FORM SECTION */}
      <motion.section
        className="relative py-24 px-4 sm:px-6 lg:px-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div className="max-w-4xl mx-auto bg-white/60 backdrop-blur-md p-10 rounded-lg shadow-[0_8px_30px_rgba(0,0,0,0.08)] border border-border">
          <motion.h2
            className="text-4xl sm:text-5xl font-light text-center mb-12 italic text-primary"
            variants={itemVariants}
          >
            We'd Love to Connect
          </motion.h2>

          <div className="grid sm:grid-cols-2 gap-8 mb-10">
            <motion.div variants={itemVariants} className="space-y-2">
              <label className="text-foreground/80 font-medium">
                Your Name
              </label>
              <input
                type="text"
                className="w-full px-4 py-3 border border-border rounded-lg bg-card focus:outline-none focus:border-primary"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => updateField("name", e.target.value)}
              />
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-2">
              <label className="text-foreground/80 font-medium">Email</label>
              <input
                type="email"
                className="w-full px-4 py-3 border border-border rounded-lg bg-card focus:outline-none focus:border-primary"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => updateField("email", e.target.value)}
              />
            </motion.div>
          </div>

          <motion.div variants={itemVariants} className="space-y-2 mb-10">
            <label className="text-foreground/80 font-medium">Message</label>
            <textarea
              rows={6}
              className="w-full px-4 py-3 border border-border rounded-lg bg-card focus:outline-none focus:border-primary"
              placeholder="Write your message here..."
              value={message}
              onChange={(e) => updateField("message", e.target.value)}
            ></textarea>
          </motion.div>

          <motion.div variants={itemVariants} className="text-center">
            <button
              onClick={handleSendEmail}
              className="px-12 py-4 bg-primary text-primary-foreground rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Send Message
            </button>
          </motion.div>
        </div>
      </motion.section>

      {/* FOOTER */}
      <footer className="border-t border-border py-10 text-center text-sm text-foreground/60">
        <p>
          &copy; {new Date().getFullYear()} The Blind Date. All rights reserved.
        </p>
        <p className="mt-2">
          <Link to="/" className="text-primary hover:underline">
            Back to Home
          </Link>
        </p>
      </footer>
    </main>
  );
};
